import { AnyAction } from 'redux'
import { getAutomationRuleActionsFromApi } from 'src/editor/shared/settings/automation-rules-settings/utils/get-automation-rule-actions-from-api'
import {
  ActionApiType,
  ActionSaveType,
} from 'src/editor/shared/types/automation-rules/actions-types'
import {
  AutomationRuleTriggerApiInterface,
  AutomationRuleTriggerInterface,
  AutomationRuleTriggersEnum,
  TriggerKeyType,
} from 'src/editor/shared/types/automation-rules/triggers-types'
import { EntityIdType } from 'src/shared/schemas/entity-id.schema'
import {
  FETCH_PAGE_AUTOMATION_RULES,
  FetchPageAutomationRules,
  REMOVE_AUTOMATION_ACTION,
  RemoveAutomationAction,
  SAVE_AUTOMATION_RULE,
  SAVE_AUTOMATION_RULES_STATE,
  SaveAutomationRule,
  SaveAutomationRulesState,
  TOGGLE_AUTOMATION_RULE_PREVENT_SAVING,
  ToggleAutomationRulePreventSaving,
} from './automationRulesActionTypes'

export interface AutomationRuleApiInterface {
  id: number
  actions: Array<ActionApiType>
  triggers: AutomationRuleTriggerApiInterface[]
}

export interface AutomationRuleInterface {
  id?: number
  automationTrigger: AutomationRuleTriggerInterface
  automationActions: ActionSaveType[]
}

interface AutomationRuleCommonArgsInterface {
  entityId: EntityIdType
  triggerKey: TriggerKeyType
  automationTriggerType: AutomationRuleTriggersEnum
  automationAction?: ActionSaveType
  preventSaving?: boolean
}

export interface AutomationRuleTogglePreventSavingInterface
  extends Omit<AutomationRuleCommonArgsInterface, 'automationAction'> {}

export interface AutomationRuleRemoveActionInterface
  extends Omit<AutomationRuleCommonArgsInterface, 'automationAction'> {
  actionId: number
}

export interface AutomationRuleSaveInterface
  extends Omit<AutomationRuleCommonArgsInterface, 'preventSaving'> {}

export interface AutomationRulesState {
  automationRules: AutomationRuleInterface[]
}

const defaultState = {
  automationRules: [],
}

const formatAutomationRuleData = ({
  entityId,
  triggerKey,
  preventSaving,
  automationTriggerType,
  automationAction,
}: AutomationRuleCommonArgsInterface): AutomationRuleInterface => {
  return {
    automationTrigger: {
      type: automationTriggerType,
      [triggerKey]: entityId,
      preventSaving: preventSaving ?? false,
    },
    automationActions: automationAction ? [automationAction] : [],
  }
}

const isRuleBeingModified = (
  rule: AutomationRuleInterface,
  payload: Pick<AutomationRuleCommonArgsInterface, 'entityId' | 'triggerKey'>,
) => rule.automationTrigger[payload.triggerKey] === payload.entityId

const updateAutomationRules = (
  payload: AutomationRuleCommonArgsInterface,
  state: AutomationRuleInterface[],
): AutomationRuleInterface[] => {
  const existingRule = state.find(rule => isRuleBeingModified(rule, payload))

  if (existingRule) {
    return state.map(rule => {
      if (isRuleBeingModified(rule, payload)) {
        const updatedAutomationActions = rule.automationActions.map(action => {
          if (action.id === payload.automationAction?.id) {
            return payload.automationAction
          }
          return action
        })
        if (
          payload.automationAction &&
          !updatedAutomationActions.find(
            action => action.id === payload.automationAction?.id,
          )
        ) {
          updatedAutomationActions.push(payload.automationAction)
        }
        return {
          ...rule,
          automationTrigger: {
            ...rule.automationTrigger,
            preventSaving: payload.preventSaving ?? false,
          },
          automationActions: updatedAutomationActions,
        }
      }
      return rule
    })
  }

  if (!payload.automationAction) {
    return state
  }

  return [...state, formatAutomationRuleData(payload)]
}

export default function automationRulesReducer(
  state: AutomationRulesState = defaultState,
  action: AnyAction,
) {
  const { type } = action

  switch (type) {
    case FETCH_PAGE_AUTOMATION_RULES:
      return {
        ...state,
        automationRules: [
          ...(action as FetchPageAutomationRules).payload.map(el => {
            return {
              id: el.id,
              automationActions: el.actions
                .map(action => getAutomationRuleActionsFromApi(action))
                .filter(Boolean),
              automationTrigger: el.triggers
                .map(trigger => ({
                  id: trigger.id,
                  type: trigger.type,
                  buttonId: trigger.buttonId,
                  checkBoxId: trigger.checkBoxId,
                  formId: trigger.formId,
                }))
                .filter(Boolean)[0],
            }
          }),
        ],
      }
    case SAVE_AUTOMATION_RULE:
      return {
        ...state,
        automationRules: updateAutomationRules(
          (action as SaveAutomationRule).payload,
          state.automationRules,
        ),
      }
    case REMOVE_AUTOMATION_ACTION: {
      const payload = (action as RemoveAutomationAction).payload
      return {
        ...state,
        automationRules: state.automationRules.map(rule => {
          if (isRuleBeingModified(rule, payload)) {
            return {
              ...rule,
              automationActions: rule.automationActions.filter(
                action => action.id !== payload.actionId,
              ),
            }
          }
          return rule
        }),
      }
    }
    case TOGGLE_AUTOMATION_RULE_PREVENT_SAVING: {
      const payload = (action as ToggleAutomationRulePreventSaving).payload
      return {
        ...state,
        automationRules: updateAutomationRules(payload, state.automationRules),
      }
    }
    case SAVE_AUTOMATION_RULES_STATE:
      return { ...(action as SaveAutomationRulesState).payload }
    default:
      return state
  }
}
